import assign from 'lodash/assign.js';
import clone from 'lodash/clone.js';

import type {
  BrowserInfo,
  HighEntropyBrowserHints,
  LowEntropyBrowserHints,
} from './browserFeatures.types';

export interface BrowserFeatureProps {
  lowEntropyHints?: LowEntropyBrowserHints;
  highEntropyHints?: HighEntropyBrowserHints;
}

export abstract class AbstractBrowserFeature implements BrowserInfo {
  protected lowEntropyHints?: LowEntropyBrowserHints;
  protected highEntropyHints?: HighEntropyBrowserHints;

  constructor(props?: BrowserFeatureProps) {
    this.lowEntropyHints = props?.lowEntropyHints;
    this.highEntropyHints = props?.highEntropyHints;
  }

  // ===========================================================================
  // Public methods.
  // ===========================================================================

  /** @override */
  public getLowEntropyHints = (): Readonly<LowEntropyBrowserHints> => {
    if (!this.lowEntropyHints) {
      this.lowEntropyHints = Object.freeze(this.computeLowEntropyHints());
    }

    return this.lowEntropyHints;
  };

  /** @override */
  public getHighEntropyHintsAsync = async (options: {
    hints: Array<keyof HighEntropyBrowserHints>;
  }): Promise<Readonly<HighEntropyBrowserHints>> => {
    if (!this.highEntropyHints) {
      this.highEntropyHints = {};
    }
    const missingHints = options.hints.filter(hint => !this.highEntropyHints![hint]);
    if (missingHints.length === 0) return this.highEntropyHints;
    const newHints = await this.computeHighEntropyHintsAsync(...missingHints);
    const updatedHints: HighEntropyBrowserHints = clone(this.highEntropyHints);
    assign(updatedHints, newHints);
    this.highEntropyHints = updatedHints;
    return Object.freeze(this.highEntropyHints);
  };

  /** @override */
  public getCachedHighEntropyHints = (): Readonly<HighEntropyBrowserHints> | undefined => {
    return this.highEntropyHints;
  };

  // ===========================================================================
  // Abstract methods.
  // ===========================================================================

  protected abstract computeLowEntropyHints(): LowEntropyBrowserHints;

  protected abstract computeHighEntropyHintsAsync(
    ...hints: Array<keyof HighEntropyBrowserHints>
  ): Promise<HighEntropyBrowserHints>;
}
